import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_URL } from './config';
import './LiveOpenPositions.css';

// Live (intraday) IBKR positions, kept fresh continuously by
// scripts/ibkr/trade_listener.py's reqPositions() subscription - a separate
// table from the daily Flex Query import's Open Positions page. This page
// is deliberately read-only and simple: it just shows whatever is currently
// in portfolio_liveopenposition, plus how long ago each row last changed,
// so it's obvious when a row is stale (listener disconnected, or the
// market's just quiet). The table itself is wiped every day right after
// the Flex Query import lands - see LiveOpenPosition's model docstring.
function LiveOpenPositions({ username, onLogout }) {
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastFetchedAt, setLastFetchedAt] = useState(null);

  const fetchPositions = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/portfolio/live-open-positions/?page_size=all`);
      setPositions(Array.isArray(response.data) ? response.data : (response.data.results || []));
      setError(null);
    } catch (err) {
      console.error('Error fetching live open positions:', err);
      setError('Failed to load live positions.');
    } finally {
      setLoading(false);
      setLastFetchedAt(new Date());
    }
  }, []);

  useEffect(() => {
    fetchPositions();
    // Refresh this page's own view every 30s - the underlying data changes
    // whenever the live listener writes a new position event, independent
    // of this polling interval, so this is just "how often the browser
    // checks", not the actual data freshness.
    const interval = setInterval(fetchPositions, 30000);
    return () => clearInterval(interval);
  }, [fetchPositions]);

  const formatAge = (isoString) => {
    if (!isoString) return '—';
    const seconds = Math.max(0, Math.floor((Date.now() - new Date(isoString).getTime()) / 1000));
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    return `${hours}h ${minutes % 60}m ago`;
  };

  const isStale = (isoString) => {
    if (!isoString) return true;
    return Date.now() - new Date(isoString).getTime() > 15 * 60 * 1000; // >15 min old
  };

  const describeContract = (p) => {
    if (p.sec_type === 'OPT') {
      const rightLabel = p.put_call === 'C' ? 'Call' : p.put_call === 'P' ? 'Put' : p.put_call;
      return `${p.underlying_symbol || p.symbol} ${p.expiry} ${p.strike} ${rightLabel}`;
    }
    return p.symbol;
  };

  return (
    <div className="live-open-positions-page">
      <div className="live-open-positions-content">
        <div className="live-open-positions-banner">
          <strong>Live positions (IBKR API, intraday)</strong> — updates continuously
          while the IBKR trade listener is connected. This is separate from the
          official <a href="/open-positions">Open Positions</a> page, which is
          sourced from the daily Flex Query import and stays the authoritative
          end-of-day record. This table is cleared every day right after that
          import completes.
          {lastFetchedAt && (
            <span className="live-open-positions-refreshed">
              Page refreshed {formatAge(lastFetchedAt.toISOString())}
            </span>
          )}
        </div>

        {loading && <div className="live-open-positions-status">Loading…</div>}
        {error && <div className="live-open-positions-status live-open-positions-error">{error}</div>}

        {!loading && !error && positions.length === 0 && (
          <div className="live-open-positions-status">
            No live positions recorded yet. Either the listener hasn't seen a position
            update since the last Flex Query import wiped this table, or it isn't
            currently connected.
          </div>
        )}

        {!loading && !error && positions.length > 0 && (
          <table className="live-open-positions-table">
            <thead>
              <tr>
                <th>Account</th>
                <th>Contract</th>
                <th>Currency</th>
                <th>Position</th>
                <th>Avg Cost</th>
                <th>Last Update</th>
              </tr>
            </thead>
            <tbody>
              {positions.map((p) => (
                <tr key={p.id} className={isStale(p.updated_at) ? 'live-row-stale' : ''}>
                  <td>{p.account_id}</td>
                  <td>{describeContract(p)}</td>
                  <td>{p.currency}</td>
                  <td>{p.position}</td>
                  <td>{p.avg_cost ?? '—'}</td>
                  <td title={p.updated_at}>
                    {formatAge(p.updated_at)}
                    {isStale(p.updated_at) && <span className="live-row-stale-badge">stale</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default LiveOpenPositions;
